import { FlowSummary, L7AccessLogSummary, InventoryCluster, InventoryNamespace,
         InventoryWorkload, InventoryService, InventoryNode,
         CloudResource, AWSConnection, OCIConnection } from '../../api/client';
import { isValidVpcId, isValidVcnId } from './utils';

// ============================================================================
// CORE NODE DATA TYPE
// ============================================================================

export interface CustomNodeData extends Record<string, unknown> {
    type: 'cluster' | 'namespace' | 'node' | 'workload' | 'service' | 'container' | 'summary' | 'external-namespace' | 'external-ip' | 'known-ip'
        | 'aws-account' | 'aws-vpc' | 'aws-resource' | 'aws-global-group'
        | 'oci-tenancy' | 'oci-vcn' | 'oci-resource' | 'oci-global-group';
    label: string;
    level: number;
    isAnimating?: boolean;
    description?: string;
    isConnected?: boolean;
    count?: number;
    kind?: string;
    clusterUID?: string;
    namespaceID?: string;
    namespaceName?: string;
    labels?: any;
    lastSeen?: string;
    originalNodes?: any[];
    groupKey?: string;
    serviceType?: string;
    clusterIp?: string;
    externalIps?: string[];
    containerType?: 'cluster' | 'namespace' | 'workload' | 'application' | 'aws-account' | 'aws-vpc' | 'aws-subnet' | 'aws-global'
        | 'oci-tenancy' | 'oci-vcn' | 'oci-subnet' | 'oci-global';
    // Flow-related properties
    externalNamespace?: string;
    externalIp?: string;
    isExternal?: boolean;
    // Known IP properties (for external IPs with friendly names)
    knownIpName?: string;
    knownIpId?: string;
    // Node-specific properties
    internalIps?: string[];
    hostname?: string;
    ready?: boolean;
    instanceType?: string;
    zone?: string;

    // =========================================================================
    // EXPANSION MODEL FIELDS
    // =========================================================================

    /** Whether this node is currently expanded (showing children) */
    isExpanded?: boolean;

    /** Whether this node can be expanded (true for clusters and namespaces) */
    isExpandable?: boolean;

    /** Whether this node is currently loading data */
    isLoading?: boolean;

    /** Number of children (shown in collapsed state, e.g., "▶ 12 namespaces") */
    childCount?: number;

    /** Depth level for nesting: 0=cluster, 1=namespace, 2=workload */
    expansionDepth?: number;

    /** Parent cluster UID (for namespaces and workloads) */
    parentClusterUID?: string;

    /** Parent namespace ID (for workloads) */
    parentNamespaceID?: string;

    // =========================================================================
    // AWS CLOUD RESOURCE FIELDS
    // =========================================================================

    /** AWS Account ID (for account, VPC, and resource nodes) */
    awsAccountId?: string;

    /** AWS Connection ID */
    awsConnectionId?: string;

    /** VPC ID (for VPC and resource nodes) */
    vpcId?: string;

    /** AWS Region */
    awsRegion?: string;

    /** Resource type in CloudFormation format (e.g., AWS::EC2::Instance) */
    resourceType?: string;

    /** Resource ARN */
    arn?: string;

    /** Subnet ID */
    subnetId?: string;

    /** Availability Zone */
    availabilityZone?: string;

    /** Private IP address */
    privateIp?: string;

    /** Public IP address */
    publicIp?: string;

    /** Whether this is a global (non-VPC) resource */
    isGlobalResource?: boolean;

    /** Parent AWS Account ID (for VPCs and resources) */
    parentAwsAccountId?: string;

    /** Parent VPC ID (for resources) */
    parentVpcId?: string;

    /** Number of VPCs in account (for account nodes) */
    vpcCount?: number;

    /** Number of resources in VPC or global group */
    resourceCount?: number;

    // =========================================================================
    // OCI CLOUD RESOURCE FIELDS
    // =========================================================================

    /** OCI Connection ID */
    ociConnectionId?: string;

    /** OCI Tenancy OCID */
    ociTenancyOcid?: string;

    /** VCN ID (for VCN and resource nodes) */
    vcnId?: string;

    /** VCN Name */
    vcnName?: string;

    /** OCI Region */
    ociRegion?: string;

    /** Compartment OCID */
    compartmentId?: string;

    /** Availability Domain (OCI equivalent of AWS Availability Zone) */
    availabilityDomain?: string;

    /** OCID - OCI Resource ID (like ARN for AWS) */
    ocid?: string;

    /** Parent OCI Tenancy OCID (for VCNs and resources) */
    parentOciTenancyOcid?: string;

    /** Parent VCN ID (for OCI resources) */
    parentVcnId?: string;

    /** Number of VCNs in tenancy (for tenancy nodes) */
    vcnCount?: number;

    // =========================================================================
    // INCIDENT FIELDS (for highlighting resources with active incidents)
    // =========================================================================

    /** Number of active incidents affecting this resource (last 4 hours) */
    incidentCount?: number;

    /** Highest severity of active incidents (critical, high, medium, low) */
    incidentSeverity?: string;

    // =========================================================================
    // CLOUD RESOURCE OBJECT (for detail dialog)
    // =========================================================================

    /** Full CloudResource object for detail dialog (only set on aws-resource and oci-resource nodes) */
    cloudResource?: CloudResource;
}

export interface EnrichedFlowSummary extends FlowSummary {
    protocols?: string[];
    ports?: number[];
    originalFlows?: FlowSummary[]; // Individual flows that make up this summary
}

// Unified type to represent both L3/L4 flows and L7 access logs for topology visualization
export type UnifiedFlowData = FlowSummary | L7AccessLogSummary;

export interface EnrichedL7AccessLogSummary extends L7AccessLogSummary {
    originalLogs?: L7AccessLogSummary[]; // Individual logs that make up this summary
}

// Updated bidirectional flow data to support both flow types
export interface BidirectionalFlowData {
    isBidirectional: true;
    flow: EnrichedFlowSummary | EnrichedL7AccessLogSummary;
    reverseFlow: EnrichedFlowSummary | EnrichedL7AccessLogSummary;
}

export interface EnrichedBidirectionalFlowData extends BidirectionalFlowData {
    originalFlows?: UnifiedFlowData[]; // Individual flows that make up the aggregated flow
    originalReverseFlows?: UnifiedFlowData[];
}

export interface ViewState {
    level: 'cluster' | 'namespace' | 'workload';
    selectedCluster?: any; // InventoryCluster
    selectedNamespace?: any; // InventoryNamespace
    breadcrumbs: Array<{ label: string; level: 'cluster' | 'namespace' | 'workload'; id?: string }>;
    aggregation?: {
        isAggregated: boolean;
        groupKey?: string;
    };
}

export const INITIAL_BREADCRUMBS: ViewState['breadcrumbs'] = [{ label: 'All Clusters', level: 'cluster', id: 'root' }];

// ============================================================================
// NEW TYPES FOR EXPANSION MODEL
// ============================================================================

/**
 * New ViewState for expansion-based navigation
 * Replaces the level-based navigation
 */
export interface ExpansionViewState {
    /** Set of expanded cluster UIDs */
    expandedClusters: Set<string>;

    /** Set of expanded namespace IDs */
    expandedNamespaces: Set<string>;

    /** Set of expanded AWS Account IDs */
    expandedAwsAccounts: Set<string>;

    /** Set of expanded VPC IDs */
    expandedVpcs: Set<string>;

    /** Set of expanded OCI Tenancy OCIDs */
    expandedOciTenancies: Set<string>;

    /** Set of expanded VCN IDs */
    expandedVcns: Set<string>;

    /** Currently focused cluster (for single-cluster focus mode) */
    focusedCluster?: string;

    /** Navigation breadcrumbs */
    breadcrumbs: ExpansionBreadcrumb[];
}

export interface ExpansionBreadcrumb {
    label: string;
    type: 'root' | 'cluster' | 'namespace' | 'aws-account' | 'aws-vpc' | 'oci-tenancy' | 'oci-vcn';
    id?: string;
}

/**
 * Cache entry for cluster data
 * Stores all data needed when a cluster is expanded
 */
export interface ClusterCacheEntry {
    /** The cluster object */
    cluster: InventoryCluster;

    /** Namespaces within this cluster */
    namespaces: InventoryNamespace[];

    /** Kubernetes nodes in this cluster */
    nodes: InventoryNode[];

    /** Namespace-to-namespace flows (aggregated) */
    flows: FlowSummary[];

    /** L7 access logs (aggregated) */
    l7Logs: L7AccessLogSummary[];

    /** Timestamp when data was loaded */
    loadedAt: number;

    /** Whether all data has been loaded */
    isComplete: boolean;
}

/**
 * Cache entry for namespace data
 * Stores all data needed when a namespace is expanded
 */
export interface NamespaceCacheEntry {
    /** The namespace object */
    namespace: InventoryNamespace;

    /** Parent cluster UID */
    clusterUID: string;

    /** Workloads in this namespace */
    workloads: InventoryWorkload[];

    /** Services in this namespace */
    services: InventoryService[];

    /** Workload-to-workload flows */
    flows: FlowSummary[];

    /** L7 access logs */
    l7Logs: L7AccessLogSummary[];

    /** Timestamp when data was loaded */
    loadedAt: number;

    /** Whether all data has been loaded */
    isComplete: boolean;
}

// ============================================================================
// AWS CLOUD RESOURCE CACHE TYPES
// ============================================================================

/**
 * Grouped cloud resources by VPC for an account
 */
export interface VPCResourceGroup {
    /** VPC ID (or 'global' for non-VPC resources) */
    vpcId: string;

    /** VPC Name (extracted from VPC resource if available) */
    vpcName?: string;

    /** Region */
    region: string;

    /** Resources in this VPC */
    resources: CloudResource[];

    /** Whether this is the global (non-VPC) group */
    isGlobal: boolean;
}

/**
 * Grouped cloud resources by subnet within a VPC
 */
export interface SubnetResourceGroup {
    /** Subnet ID (or 'no-subnet' for VPC resources without subnet) */
    subnetId: string;

    /** Availability Zone */
    availabilityZone?: string;

    /** Resources in this subnet */
    resources: CloudResource[];
}

/**
 * Helper to group resources by subnet within a VPC
 * Returns resources organized by their subnet_id
 */
export function groupResourcesBySubnet(resources: CloudResource[]): SubnetResourceGroup[] {
    const subnetMap = new Map<string, CloudResource[]>();
    const noSubnetResources: CloudResource[] = [];

    resources.forEach(resource => {
        if (resource.subnet_id) {
            const existing = subnetMap.get(resource.subnet_id) || [];
            existing.push(resource);
            subnetMap.set(resource.subnet_id, existing);
        } else {
            // Resources without subnet (e.g., NAT Gateways in VPC but not in specific subnet)
            noSubnetResources.push(resource);
        }
    });

    const groups: SubnetResourceGroup[] = [];

    // Add subnet groups
    subnetMap.forEach((subnetResources, subnetId) => {
        // Get AZ from first resource that has it
        const az = subnetResources.find(r => r.availability_zone)?.availability_zone;
        groups.push({
            subnetId,
            availabilityZone: az,
            resources: subnetResources
        });
    });

    // Sort subnets by ID for consistent ordering
    groups.sort((a, b) => a.subnetId.localeCompare(b.subnetId));

    // Add resources without subnet at the end (if any)
    if (noSubnetResources.length > 0) {
        groups.push({
            subnetId: 'no-subnet',
            resources: noSubnetResources
        });
    }

    return groups;
}

/**
 * Cache entry for AWS account data
 * Stores all cloud resources for an account, grouped by VPC
 */
export interface AwsAccountCacheEntry {
    /** AWS Connection object */
    connection: AWSConnection;

    /** AWS Account ID */
    accountId: string;

    /** All cloud resources for this account */
    resources: CloudResource[];

    /** Resources grouped by VPC */
    vpcGroups: VPCResourceGroup[];

    /** List of unique VPC IDs in this account */
    vpcIds: string[];

    /** VPC Flow Log summaries for this account (from account-wide flow log collection) */
    flows: FlowSummary[];

    /** IP address to resource ID lookup map for VPC Flow Log correlation */
    ipToResourceMap: Map<string, string>;

    /** Timestamp when data was loaded */
    loadedAt: number;

    /** Whether all data has been loaded */
    isComplete: boolean;
}

/**
 * Helper to group cloud resources by VPC
 */
export function groupResourcesByVpc(resources: CloudResource[]): VPCResourceGroup[] {
    const vpcMap = new Map<string, CloudResource[]>();
    const globalResources: CloudResource[] = [];

    // Group resources
    resources.forEach(resource => {
        if (isValidVpcId(resource.vpc_id)) {
            const existing = vpcMap.get(resource.vpc_id!) || [];
            existing.push(resource);
            vpcMap.set(resource.vpc_id!, existing);
        } else {
            // Resources without a valid VPC ID go to global resources
            // This includes S3 buckets, IAM roles, Route53, etc.
            globalResources.push(resource);
        }
    });

    const groups: VPCResourceGroup[] = [];

    // Add VPC groups
    vpcMap.forEach((vpcResources, vpcId) => {
        // Try to find VPC name from the VPC resource itself
        const vpcResource = vpcResources.find(r => r.resource_type === 'AWS::EC2::VPC');
        const region = vpcResources[0]?.region || 'unknown';

        groups.push({
            vpcId,
            vpcName: vpcResource?.name || undefined,
            region,
            resources: vpcResources,
            isGlobal: false,
        });
    });

    // Sort VPCs by name/id
    groups.sort((a, b) => (a.vpcName || a.vpcId).localeCompare(b.vpcName || b.vpcId));

    // Add global resources group if any exist
    if (globalResources.length > 0) {
        groups.unshift({
            vpcId: 'global',
            vpcName: 'Global Resources',
            region: 'global',
            resources: globalResources,
            isGlobal: true,
        });
    }

    return groups;
}

/**
 * Build IP-to-resource lookup map for VPC Flow Log correlation
 * Maps all known IPs (private, public, additional) to resource IDs
 */
export function buildIpToResourceMap(resources: CloudResource[]): Map<string, string> {
    const ipMap = new Map<string, string>();

    resources.forEach(resource => {
        // Add primary private IP
        if (resource.private_ip) {
            ipMap.set(resource.private_ip, resource.id);
        }

        // Add public IP
        if (resource.public_ip) {
            ipMap.set(resource.public_ip, resource.id);
        }

        // Add additional IPs (for multi-ENI resources like NLBs, NAT Gateways)
        if (resource.additional_ips) {
            resource.additional_ips.forEach(ip => {
                ipMap.set(ip.ip_address, resource.id);
            });
        }
    });

    return ipMap;
}

// ============================================================================
// OCI CLOUD RESOURCE CACHE TYPES
// ============================================================================

/**
 * Grouped cloud resources by VCN for a tenancy
 */
export interface VCNResourceGroup {
    /** VCN ID (or 'global' for non-VCN resources) */
    vcnId: string;

    /** VCN Name (extracted from VCN resource if available) */
    vcnName?: string;

    /** Region */
    region: string;

    /** Resources in this VCN */
    resources: CloudResource[];

    /** Whether this is the global (non-VCN) group */
    isGlobal: boolean;
}

/**
 * Cache entry for OCI tenancy data
 * Stores all cloud resources for a tenancy, grouped by VCN
 */
export interface OciTenancyCacheEntry {
    /** OCI Connection object */
    connection: OCIConnection;

    /** OCI Tenancy OCID */
    tenancyOcid: string;

    /** All cloud resources for this tenancy */
    resources: CloudResource[];

    /** Resources grouped by VCN */
    vcnGroups: VCNResourceGroup[];

    /** List of unique VCN IDs in this tenancy */
    vcnIds: string[];

    /** VCN Flow Log summaries for this tenancy (from tenancy-wide flow log collection) */
    flows: FlowSummary[];

    /** IP address to resource ID lookup map for VCN Flow Log correlation */
    ipToResourceMap: Map<string, string>;

    /** Timestamp when data was loaded */
    loadedAt: number;

    /** Whether all data has been loaded */
    isComplete: boolean;
}

/**
 * Helper to group cloud resources by VCN
 */
export function groupResourcesByVcn(resources: CloudResource[]): VCNResourceGroup[] {
    const vcnMap = new Map<string, CloudResource[]>();
    const globalResources: CloudResource[] = [];

    // Group resources
    resources.forEach(resource => {
        if (isValidVcnId(resource.vpc_id)) {
            // OCI stores VCN ID in vpc_id field
            const existing = vcnMap.get(resource.vpc_id!) || [];
            existing.push(resource);
            vcnMap.set(resource.vpc_id!, existing);
        } else {
            // Resources without a valid VCN ID go to global resources
            // This includes Identity users, Object Storage buckets, etc.
            globalResources.push(resource);
        }
    });

    const groups: VCNResourceGroup[] = [];

    // Add VCN groups
    vcnMap.forEach((vcnResources, vcnId) => {
        // Try to find VCN name from the VCN resource itself
        const vcnResource = vcnResources.find(r => r.resource_type === 'OCI::Core::VCN');
        const region = vcnResources[0]?.region || 'unknown';

        groups.push({
            vcnId,
            vcnName: vcnResource?.name || undefined,
            region,
            resources: vcnResources,
            isGlobal: false,
        });
    });

    // Sort VCNs by name/id
    groups.sort((a, b) => (a.vcnName || a.vcnId).localeCompare(b.vcnName || b.vcnId));

    // Add global resources group if any exist
    if (globalResources.length > 0) {
        groups.unshift({
            vcnId: 'global',
            vcnName: 'Global Resources',
            region: 'global',
            resources: globalResources,
            isGlobal: true,
        });
    }

    return groups;
}

/**
 * Layout node used in layout calculation
 * Intermediate representation before converting to React Flow nodes
 */
export interface LayoutNode {
    id: string;
    type: CustomNodeData['type'] | 'container';
    isExpanded: boolean;
    isExpandable: boolean;

    /** Calculated size (width, height) */
    size: { width: number; height: number };

    /** Calculated position (x, y) relative to parent */
    position: { x: number; y: number };

    /** Absolute position on canvas (for external node positioning) */
    absolutePosition?: { x: number; y: number };

    /** Parent node ID (for React Flow nesting) */
    parentId?: string;

    /** Child layout nodes */
    children?: LayoutNode[];

    /** Original data for this node */
    data?: any;
}

/**
 * Canvas bounds for positioning external nodes
 */
export interface CanvasBounds {
    minX: number;
    minY: number;
    maxX: number;
    maxY: number;
    width: number;
    height: number;
    centerX: number;
    centerY: number;
}

// ============================================================================
// INITIAL STATES
// ============================================================================

export const INITIAL_EXPANSION_STATE: ExpansionViewState = {
    expandedClusters: new Set(),
    expandedNamespaces: new Set(),
    expandedAwsAccounts: new Set(),
    expandedVpcs: new Set(),
    expandedOciTenancies: new Set(),
    expandedVcns: new Set(),
    breadcrumbs: [{ label: 'Infrastructure', type: 'root' }]
};

export const INITIAL_EXPANSION_BREADCRUMBS: ExpansionBreadcrumb[] = [
    { label: 'All Clusters', type: 'root', id: 'root' }
];

// ============================================================================
// TYPE GUARDS
// ============================================================================

export const isClusterNode = (data: CustomNodeData): boolean =>
    data.type === 'cluster';

export const isNamespaceNode = (data: CustomNodeData): boolean =>
    data.type === 'namespace';

export const isExpandableNode = (data: CustomNodeData): boolean =>
    data.type === 'cluster' || data.type === 'namespace';

export const isWorkloadNode = (data: CustomNodeData): boolean =>
    data.type === 'workload';

export const isServiceNode = (data: CustomNodeData): boolean =>
    data.type === 'service';

export const isExternalNode = (data: CustomNodeData): boolean =>
    data.type === 'external-ip' || data.type === 'external-namespace';

export const isKnownIpNode = (data: CustomNodeData): boolean =>
    data.type === 'known-ip';

// AWS Resource type guards
export const isAwsAccountNode = (data: CustomNodeData): boolean =>
    data.type === 'aws-account';

export const isAwsVpcNode = (data: CustomNodeData): boolean =>
    data.type === 'aws-vpc';

export const isAwsResourceNode = (data: CustomNodeData): boolean =>
    data.type === 'aws-resource';

export const isAwsGlobalGroupNode = (data: CustomNodeData): boolean =>
    data.type === 'aws-global-group';

export const isAwsNode = (data: CustomNodeData): boolean =>
    data.type === 'aws-account' || data.type === 'aws-vpc' ||
    data.type === 'aws-resource' || data.type === 'aws-global-group';

export const isAwsExpandableNode = (data: CustomNodeData): boolean =>
    data.type === 'aws-account' || data.type === 'aws-vpc' || data.type === 'aws-global-group';

// OCI Resource type guards
export const isOciTenancyNode = (data: CustomNodeData): boolean =>
    data.type === 'oci-tenancy';

export const isOciVcnNode = (data: CustomNodeData): boolean =>
    data.type === 'oci-vcn';

export const isOciResourceNode = (data: CustomNodeData): boolean =>
    data.type === 'oci-resource';

export const isOciGlobalGroupNode = (data: CustomNodeData): boolean =>
    data.type === 'oci-global-group';

export const isOciNode = (data: CustomNodeData): boolean =>
    data.type === 'oci-tenancy' || data.type === 'oci-vcn' ||
    data.type === 'oci-resource' || data.type === 'oci-global-group';

export const isOciExpandableNode = (data: CustomNodeData): boolean =>
    data.type === 'oci-tenancy' || data.type === 'oci-vcn' || data.type === 'oci-global-group';

// Cloud node helpers (both AWS and OCI)
export const isCloudAccountNode = (data: CustomNodeData): boolean =>
    data.type === 'aws-account' || data.type === 'oci-tenancy';

export const isCloudNetworkNode = (data: CustomNodeData): boolean =>
    data.type === 'aws-vpc' || data.type === 'oci-vcn';

export const isCloudGlobalGroupNode = (data: CustomNodeData): boolean =>
    data.type === 'aws-global-group' || data.type === 'oci-global-group';

export const isCloudResourceLeafNode = (data: CustomNodeData): boolean =>
    data.type === 'aws-resource' || data.type === 'oci-resource';

export const isAnyCloudNode = (data: CustomNodeData): boolean =>
    isAwsNode(data) || isOciNode(data);

// ============================================================================
// CLOUD RESOURCE KIND CONSTANTS
// ============================================================================

/**
 * Canonical list of cloud resource kinds for endpoint classification.
 * Used to distinguish cloud resources from external/in-cluster endpoints.
 * Includes variations to handle different naming conventions.
 */
export const CLOUD_RESOURCE_KINDS = new Set([
    // AWS EC2
    'EC2Instance',
    'Instance',
    // AWS RDS
    'RDSInstance',
    // AWS EKS
    'EKSCluster',
    // AWS Lambda (both naming conventions)
    'Lambda',
    'LambdaFunction',
    // AWS ECS
    'ECSService',
    'ECSCluster',
    // NAT Gateway (AWS and OCI)
    'NatGateway',
    'NATGateway',
    // VPC Endpoint
    'VPCEndpoint',
    // Load Balancers (AWS and OCI)
    'LoadBalancer',
    'ALB',
    'NLB',
    // Networking
    'SecurityGroup',
    'Subnet',
    'VPC',
    // AWS Storage
    'S3Bucket',
    'DynamoDBTable',
    // OCI Compute
    'ComputeInstance',
    // OCI Database
    'DBSystem',
    'AutonomousDB',
    // OCI Container
    'OKECluster',
    // OCI generic
    'CloudResource',
]);

/**
 * Check if a kind represents a cloud resource
 */
export function isCloudResourceKind(kind: string): boolean {
    return CLOUD_RESOURCE_KINDS.has(kind);
}

// ============================================================================
// AWS RESOURCE TYPE HELPERS
// ============================================================================

/** Get a friendly display name for an AWS resource type */
export function getAwsResourceTypeName(resourceType: string): string {
    const typeMap: Record<string, string> = {
        'AWS::EC2::Instance': 'EC2 Instance',
        'AWS::EC2::SecurityGroup': 'Security Group',
        'AWS::EC2::VPC': 'VPC',
        'AWS::EC2::Subnet': 'Subnet',
        'AWS::RDS::DBInstance': 'RDS Database',
        'AWS::Lambda::Function': 'Lambda Function',
        'AWS::EKS::Cluster': 'EKS Cluster',
        'AWS::ECS::Cluster': 'ECS Cluster',
        'AWS::ECS::Service': 'ECS Service',
        'AWS::S3::Bucket': 'S3 Bucket',
        'AWS::IAM::User': 'IAM User',
        'AWS::IAM::Role': 'IAM Role',
        'AWS::IAM::Policy': 'IAM Policy',
        'AWS::DynamoDB::Table': 'DynamoDB Table',
        'AWS::ElasticLoadBalancingV2::LoadBalancer': 'Load Balancer',
        'AWS::EC2::VPCEndpoint': 'VPC Endpoint',
    };
    return typeMap[resourceType] || resourceType.split('::').pop() || resourceType;
}

/** Get an icon identifier for an AWS resource type */
export function getAwsResourceIcon(resourceType: string): string {
    const iconMap: Record<string, string> = {
        'AWS::EC2::Instance': 'ec2',
        'AWS::EC2::SecurityGroup': 'security-group',
        'AWS::EC2::VPC': 'vpc',
        'AWS::EC2::Subnet': 'subnet',
        'AWS::RDS::DBInstance': 'rds',
        'AWS::Lambda::Function': 'lambda',
        'AWS::EKS::Cluster': 'eks',
        'AWS::ECS::Cluster': 'ecs',
        'AWS::ECS::Service': 'ecs-service',
        'AWS::S3::Bucket': 's3',
        'AWS::IAM::User': 'iam-user',
        'AWS::IAM::Role': 'iam-role',
        'AWS::IAM::Policy': 'iam-policy',
        'AWS::DynamoDB::Table': 'dynamodb',
        'AWS::ElasticLoadBalancingV2::LoadBalancer': 'elb',
        'AWS::EC2::VPCEndpoint': 'vpc-endpoint',
    };
    return iconMap[resourceType] || 'aws-resource';
}

// ============================================================================
// OCI RESOURCE TYPE HELPERS
// ============================================================================

/** Get a friendly display name for an OCI resource type */
export function getOciResourceTypeName(resourceType: string): string {
    const typeMap: Record<string, string> = {
        'OCI::Compute::Instance': 'Compute Instance',
        // Core networking (actual types from OCI SDK)
        'OCI::Core::VCN': 'VCN',
        'OCI::Core::Vcn': 'VCN',
        'OCI::Core::Subnet': 'Subnet',
        'OCI::Core::SecurityList': 'Security List',
        'OCI::Core::NetworkSecurityGroup': 'Network Security Group',
        'OCI::Core::RouteTable': 'Route Table',
        'OCI::Core::InternetGateway': 'Internet Gateway',
        'OCI::Core::NatGateway': 'NAT Gateway',
        'OCI::Core::ServiceGateway': 'Service Gateway',
        // Database
        'OCI::Database::AutonomousDatabase': 'Autonomous DB',
        'OCI::Database::DBSystem': 'DB System',
        'OCI::NoSQL::Table': 'NoSQL Table',
        // Containers & Kubernetes
        'OCI::Container::Cluster': 'OKE Cluster',
        'OCI::Container::NodePool': 'Node Pool',
        'OCI::ContainerInstances::ContainerInstance': 'Container Instance',
        // Serverless Functions
        'OCI::Functions::Application': 'Functions App',
        'OCI::Functions::Function': 'Function',
        // Storage
        'OCI::ObjectStorage::Bucket': 'Object Storage',
        // Load Balancer
        'OCI::LoadBalancer::LoadBalancer': 'Load Balancer',
        // Identity
        'OCI::Identity::User': 'IAM User',
        'OCI::Identity::Group': 'IAM Group',
        'OCI::Identity::Policy': 'IAM Policy',
        'OCI::Identity::Compartment': 'Compartment',
    };
    return typeMap[resourceType] || resourceType.split('::').pop() || resourceType;
}

/** Get an icon identifier for an OCI resource type */
export function getOciResourceIcon(resourceType: string): string {
    const iconMap: Record<string, string> = {
        'OCI::Compute::Instance': 'compute',
        // Core networking (actual types from OCI SDK)
        'OCI::Core::VCN': 'vcn',
        'OCI::Core::Vcn': 'vcn',
        'OCI::Core::Subnet': 'subnet',
        'OCI::Core::SecurityList': 'security-list',
        'OCI::Core::NetworkSecurityGroup': 'nsg',
        'OCI::Core::RouteTable': 'route-table',
        'OCI::Core::InternetGateway': 'internet-gateway',
        'OCI::Core::NatGateway': 'nat-gateway',
        'OCI::Core::ServiceGateway': 'service-gateway',
        // Database
        'OCI::Database::AutonomousDatabase': 'autonomous-db',
        'OCI::Database::DBSystem': 'db-system',
        'OCI::NoSQL::Table': 'nosql-table',
        // Containers & Kubernetes
        'OCI::Container::Cluster': 'oke',
        'OCI::Container::NodePool': 'node-pool',
        'OCI::ContainerInstances::ContainerInstance': 'container-instance',
        // Serverless Functions
        'OCI::Functions::Application': 'functions-app',
        'OCI::Functions::Function': 'function',
        // Storage
        'OCI::ObjectStorage::Bucket': 'object-storage',
        // Load Balancer
        'OCI::LoadBalancer::LoadBalancer': 'load-balancer',
        // Identity
        'OCI::Identity::User': 'identity-user',
        'OCI::Identity::Group': 'identity-group',
        'OCI::Identity::Policy': 'identity-policy',
        'OCI::Identity::Compartment': 'compartment',
    };
    return iconMap[resourceType] || 'oci-resource';
}
